<template>
  <v-container fluid class="pa-4 pa-md-6">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div>
          <h1 class="dashboard-title">Salud del Inventario</h1>
          <p class="text-caption grey--text">Stock, cobertura, rotaci&oacute;n y clasificaci&oacute;n ABC</p>
        </div>
        <div class="d-flex align-center ga-2 mt-2 flex-wrap">
          <v-text-field v-model="dateFrom" label="ABC Desde" outlined dense type="date" hide-details class="date-input"></v-text-field>
          <v-text-field v-model="dateTo" label="ABC Hasta" outlined dense type="date" hide-details class="date-input"></v-text-field>
          <v-btn color="primary" @click="loadData" :loading="loading" depressed small>
            <v-icon left small>mdi-refresh</v-icon> Actualizar
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- KPI Cards -->
    <v-row class="mb-4">
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Unidades en Stock" :value="data.unidades" format="number" icon="mdi-package-variant-closed" gradient="success-gradient" :subtitle="data.venta_diaria + ' und/d\u00eda promedio'" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <KpiDeltaCard label="Valor a Costo" :value="data.val_costo" format="currency" icon="mdi-currency-usd" gradient="primary-gradient" />
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex warning-gradient"><v-icon color="white">mdi-calendar-clock</v-icon></div>
            <div class="kpi-content">
              <div class="kpi-label">D&iacute;as de Inventario</div>
              <div class="kpi-value">
                <v-chip :color="data.dos < 60 ? 'success' : data.dos < 120 ? 'warning' : 'error'" text-color="white" small>
                  {{ data.dos >= 999 ? 'Sin venta' : data.dos + 'd' }}
                </v-chip>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="6" sm="6" lg="3">
        <v-card class="kpi-card elevation-0" outlined>
          <v-card-text class="pa-3 pa-sm-5">
            <div class="kpi-icon d-none d-sm-flex info-gradient"><v-icon color="white">mdi-sync</v-icon></div>
            <div class="kpi-content">
              <div class="kpi-label">Rotaci&oacute;n Anual</div>
              <div class="kpi-value">{{ data.turnover }}x</div>
              <div class="kpi-subtitle d-none d-sm-flex">S/S Ratio: {{ data.stock_to_sales }}:1</div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dead Stock Summary -->
    <v-row class="mb-4" v-if="data.dead_stock_total > 0">
      <v-col cols="12">
        <v-alert type="warning" outlined dense class="mb-0">
          <strong>Dead Stock:</strong> {{ formatCurrency(data.dead_stock_total) }} en inventario sin movimiento ({{ data.dead_stock_pct }}% del valor total a costo)
          &bull; {{ data.dead_stock.length }} productos sin venta en 90+ d&iacute;as
        </v-alert>
      </v-col>
    </v-row>

    <!-- Inventario por Categoría Chart -->
    <v-row class="mb-4">
      <v-col cols="12">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="primary">mdi-chart-bar</v-icon>
            Inventario Valorizado por Categor&iacute;a
          </v-card-title>
          <v-card-text>
            <GChart
              v-if="invByCategoryChart.length > 1"
              type="ColumnChart"
              :data="invByCategoryChart"
              :options="barChartOptions"
              class="chart-container"
            />
            <div v-else class="text-center grey--text pa-6">Sin datos</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Cobertura + Dead Stock -->
    <v-row class="mb-4">
      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="info">mdi-shield-check</v-icon>
            Cobertura por Categor&iacute;a
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="coverageHeaders"
            :items="data.coverage"
            :loading="loading"
            dense
            hide-default-footer
            :items-per-page="20"
            class="analyst-table"
            no-data-text="Sin datos"
          >
            <template v-slot:item.dias_cobertura="{ item }">
              <v-chip :color="item.dias_cobertura > 120 ? 'error' : item.dias_cobertura > 60 ? 'warning' : 'success'" text-color="white" x-small>
                {{ item.dias_cobertura >= 999 ? 'Sin venta' : item.dias_cobertura + 'd' }}
              </v-chip>
            </template>
            <template v-slot:item.venta_diaria="{ item }">{{ parseFloat(item.venta_diaria).toFixed(1) }}</template>
            <template v-slot:item.sell_through="{ item }">{{ item.sell_through || 0 }}%</template>
            <template v-slot:item.turnover="{ item }">{{ item.turnover || 0 }}</template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.coverage.length" class="text-center grey--text pa-4">Sin datos</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.coverage" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.categoria }}</v-list-item-title>
                  <v-list-item-subtitle>Stock: {{ item.stock_actual }} &bull; ST: {{ item.sell_through || 0 }}% &bull; Rot: {{ item.turnover || 0 }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="ma-0">
                  <v-chip :color="item.dias_cobertura > 120 ? 'error' : item.dias_cobertura > 60 ? 'warning' : 'success'" text-color="white" x-small>
                    {{ item.dias_cobertura >= 999 ? 'Sin venta' : item.dias_cobertura + 'd' }}
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>

      <v-col cols="12" md="6">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
            Dead Stock (90+ d&iacute;as sin venta)
            <v-spacer></v-spacer>
            <v-chip small outlined>{{ data.dead_stock.length }} productos</v-chip>
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="deadStockHeaders"
            :items="data.dead_stock"
            :loading="loading"
            dense
            :items-per-page="10"
            class="analyst-table"
            no-data-text="Sin productos sin rotaci&oacute;n"
          >
            <template v-slot:item.valor_muerto="{ item }">{{ formatCurrency(item.valor_muerto) }}</template>
            <template v-slot:item.dias_sin_venta="{ item }">
              <v-chip :color="item.dias_sin_venta > 180 ? 'error' : 'warning'" text-color="white" x-small>{{ item.dias_sin_venta }}d</v-chip>
            </template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!data.dead_stock.length" class="text-center grey--text pa-4">Sin productos sin rotaci&oacute;n</div>
            <template v-else>
              <v-list-item v-for="(item, i) in data.dead_stock.slice(0, 10)" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.marca }} - {{ item.refe }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.cantidad }} und &bull; {{ item.dias_sin_venta }}d sin venta</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="ma-0">
                  <div class="text-body-2 font-weight-bold error--text">{{ formatCurrency(item.valor_muerto) }}</div>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>

    <!-- ABC Analysis -->
    <v-row>
      <v-col cols="12">
        <v-card class="elevation-0" outlined>
          <v-card-title class="section-title pa-4 pb-2">
            <v-icon class="mr-2" color="purple">mdi-sort-descending</v-icon>
            Clasificaci&oacute;n ABC
            <v-spacer></v-spacer>
            <div class="d-flex ga-2">
              <v-chip small color="success" text-color="white">A: {{ abcSummary.a }} items (80%)</v-chip>
              <v-chip small color="warning" text-color="white">B: {{ abcSummary.b }} items (15%)</v-chip>
              <v-chip small color="error" text-color="white">C: {{ abcSummary.c }} items (5%)</v-chip>
            </div>
          </v-card-title>
          <v-data-table
            v-if="!isMobile"
            :headers="abcHeaders"
            :items="abcClassified"
            :loading="loading"
            dense
            :items-per-page="15"
            class="analyst-table"
            no-data-text="Sin datos para el periodo"
          >
            <template v-slot:item.ventas="{ item }">{{ formatCurrency(item.ventas) }}</template>
            <template v-slot:item.pct_acum="{ item }">{{ item.pct_acum }}%</template>
            <template v-slot:item.clase="{ item }">
              <v-chip :color="item.clase === 'A' ? 'success' : item.clase === 'B' ? 'warning' : 'error'" text-color="white" x-small>{{ item.clase }}</v-chip>
            </template>
          </v-data-table>
          <v-list v-else dense class="pa-0">
            <div v-if="loading" class="text-center pa-4"><v-progress-circular indeterminate size="24"></v-progress-circular></div>
            <div v-else-if="!abcClassified.length" class="text-center grey--text pa-4">Sin datos para el periodo</div>
            <template v-else>
              <v-list-item v-for="(item, i) in abcClassified.slice(0, 15)" :key="i" class="mobile-item">
                <v-list-item-content>
                  <v-list-item-title class="text-body-2 font-weight-medium">{{ item.marca }} - {{ item.refe }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.qty }} und &bull; {{ formatCurrency(item.ventas) }} &bull; {{ item.pct_acum }}%</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="ma-0">
                  <v-chip :color="item.clase === 'A' ? 'success' : item.clase === 'B' ? 'warning' : 'error'" text-color="white" x-small>{{ item.clase }}</v-chip>
                </v-list-item-action>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { webserver, formatCurrency } from "@/services/webserver.js";
import { GChart } from "vue-google-charts";
import KpiDeltaCard from "@/components/KpiDeltaCard.vue";

export default {
  name: "InventoryAnalysis",
  components: { GChart, KpiDeltaCard },
  data() {
    return {
      loading: false,
      dateFrom: new Date(Date.now() - 90 * 86400000).toISOString().substr(0, 10),
      dateTo: new Date().toISOString().substr(0, 10),
      data: {
        unidades: 0, val_costo: 0, val_precio: 0, dos: 0, venta_diaria: 0,
        turnover: 0, stock_to_sales: 0,
        dead_stock_total: 0, dead_stock_pct: 0,
        inventory_by_category: [], dead_stock: [], coverage: [], abc_data: [],
      },
      coverageHeaders: [
        { text: "Categor\u00eda", value: "categoria", sortable: false },
        { text: "Stock", value: "stock_actual", align: "end" },
        { text: "Venta/d\u00eda", value: "venta_diaria", align: "end" },
        { text: "Cobertura", value: "dias_cobertura", align: "center" },
        { text: "Sell-Through", value: "sell_through", align: "center" },
        { text: "Rotaci\u00f3n", value: "turnover", align: "center" },
      ],
      deadStockHeaders: [
        { text: "Marca", value: "marca", sortable: false },
        { text: "Ref", value: "refe", sortable: false },
        { text: "Color", value: "color", sortable: false },
        { text: "Cant.", value: "cantidad", align: "end" },
        { text: "Valor Muerto", value: "valor_muerto", align: "end" },
        { text: "D\u00edas", value: "dias_sin_venta", align: "center" },
      ],
      abcHeaders: [
        { text: "EAN", value: "ean", sortable: false },
        { text: "Marca", value: "marca", sortable: false },
        { text: "Ref", value: "refe", sortable: false },
        { text: "Cant.", value: "qty", align: "end" },
        { text: "Ventas", value: "ventas", align: "end" },
        { text: "% Acum.", value: "pct_acum", align: "end" },
        { text: "Clase", value: "clase", align: "center" },
      ],
      barChartOptions: {
        legend: { position: "top" },
        colors: ["#1a73e8", "#34a853"],
        chartArea: { width: "85%", height: "70%" },
        hAxis: { textStyle: { fontSize: 10 }, slantedText: true },
        vAxis: { format: "short" },
        height: 320,
        isStacked: false,
      },
    };
  },
  computed: {
    isMobile() { return this.$vuetify.breakpoint.xs; },
    invByCategoryChart() {
      if (!this.data.inventory_by_category.length) return [["Categor\u00eda", "Costo", "Precio"]];
      return [
        ["Categor\u00eda", "Costo", "Precio"],
        ...this.data.inventory_by_category.map((d) => [
          d.categoria, parseFloat(d.val_costo), parseFloat(d.val_precio),
        ]),
      ];
    },
    abcClassified() {
      const items = (this.data.abc_data || []).map((d) => ({
        ...d, ventas: parseFloat(d.ventas), qty: parseInt(d.qty),
      }));
      const totalVentas = items.reduce((s, i) => s + i.ventas, 0);
      if (totalVentas === 0) return [];
      let acum = 0;
      return items.map((item) => {
        acum += item.ventas;
        const pct = (acum / totalVentas) * 100;
        return { ...item, pct_acum: pct.toFixed(1), clase: pct <= 80 ? "A" : pct <= 95 ? "B" : "C" };
      });
    },
    abcSummary() {
      const classified = this.abcClassified;
      return {
        a: classified.filter((i) => i.clase === "A").length,
        b: classified.filter((i) => i.clase === "B").length,
        c: classified.filter((i) => i.clase === "C").length,
      };
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    formatCurrency,
    loadData() {
      this.loading = true;
      const store = this.$store.state.profile.store_id || window.store?.store_id;
      webserver("get_inventory_analysis", { store, date_from: this.dateFrom, date_to: this.dateTo }, (res) => {
        this.loading = false;
        if (res && !res.error) {
          this.data = { ...this.data, ...res };
        }
      });
    },
  },
};
</script>

<style scoped>
.dashboard-title { font-size: 1.5rem; font-weight: 700; color: #424242; }
.date-input { max-width: 140px; }
.kpi-card { border-radius: 8px; transition: box-shadow 0.2s ease; }
.kpi-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.08) !important; }
.kpi-card .v-card__text { display: flex; align-items: center; gap: 16px; }
.kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.success-gradient { background: linear-gradient(135deg, #1e8e3e, #34a853); }
.primary-gradient { background: linear-gradient(135deg, #1a73e8, #4285f4); }
.warning-gradient { background: linear-gradient(135deg, #f9ab00, #fbbc04); }
.info-gradient { background: linear-gradient(135deg, #1967d2, #4285f4); }
.kpi-label { font-size: 0.75rem; font-weight: 500; color: #5f6368; text-transform: uppercase; letter-spacing: 0.5px; }
.kpi-value { font-size: 1.25rem; font-weight: 700; color: #202124; line-height: 1.3; }
.kpi-subtitle { font-size: 0.75rem; color: #80868b; margin-top: 2px; }
.section-title { font-size: 0.9375rem; font-weight: 600; color: #424242; }
.analyst-table { font-size: 0.8125rem; }
.chart-container { width: 100%; min-height: 280px; }
.mobile-item { min-height: 48px; border-bottom: 1px solid #f0f0f0; }
.mobile-item:last-child { border-bottom: none; }
@media (max-width: 600px) {
  .kpi-value { font-size: 1rem; }
  .date-input { max-width: 110px; }
}
</style>
